import { useControlsStore, ControlsState } from '../store/controlsSlice';
import { useInputSourceStore } from '../store/inputSourceSlice';
import {
  DungeonAction,
  GameplayContext,
  InterfaceAction,
} from '../data/controls.config';
import {
  ActionMapping,
  GamepadBinding,
  KeyboardBinding,
} from '../types/controls.types';

type AnyAction = InterfaceAction | DungeonAction;

export type StackContext = GameplayContext | 'key-capture';
export type EntryId = number;
export type ActionKind = 'press' | 'repeat';

export type RawInput =
  | { kind: 'keyboard'; code: string; modifiers: string[] }
  | { kind: 'gamepad'; index: number };

export interface ContextCallbacks {
  onAction?: (action: AnyAction, kind: ActionKind) => void;
  onSuspended?: () => void;
  onCapture?: (raw: RawInput) => void;
  repeatable?: ReadonlyArray<AnyAction>;
}

interface StackEntry {
  id: EntryId;
  context: StackContext;
  callbacks: ContextCallbacks;
  disabled: boolean;
}

const AXIS_DEADZONE = 0.4;

class InputService {
  private readonly keysDown = new Set<string>();
  private readonly keysJustPressed = new Set<string>();
  private readonly keysJustReleased = new Set<string>();

  private readonly gpButtonsDown = new Set<number>();
  private readonly gpButtonsJustPressed = new Set<number>();
  private readonly gpAxes: number[] = [];
  private readonly gpPrevAxesActive = new Set<string>();

  private readonly stack: StackEntry[] = [];
  private nextEntryId = 1;

  // During a rebind `key-capture`, a lone modifier keydown is deferred: it holds
  // here until its keyup (→ lone-modifier binding) or until another key is
  // pressed (→ combo, committed at that keydown). Lets the user bind both
  // "Shift alone" and "Shift + key".
  private captureCandidate: string | null = null;

  private readonly hotkeys = new Map<string, () => void>();

  registerHotkey(code: string, handler: () => void): () => void {
    this.hotkeys.set(code, handler);
    return () => {
      if (this.hotkeys.get(code) === handler) {
        this.hotkeys.delete(code);
      }
    };
  }

  private rafId: number | null = null;
  private started = false;

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    document.addEventListener('keydown', this.onKeyDown, { capture: true });
    document.addEventListener('keyup', this.onKeyUp, { capture: true });
    window.addEventListener('blur', this.onBlur);

    this.pollLoop();
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;

    document.removeEventListener('keydown', this.onKeyDown, { capture: true });
    document.removeEventListener('keyup', this.onKeyUp, { capture: true });
    window.removeEventListener('blur', this.onBlur);

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  endFrame(): void {
    this.keysJustPressed.clear();
    this.keysJustReleased.clear();
    this.gpButtonsJustPressed.clear();
  }

  pushContext(
    context: StackContext,
    callbacks: ContextCallbacks = {},
    disabled = false,
  ): EntryId {
    if (context === 'key-capture') {
      this.captureCandidate = null;
    }
    const id = this.nextEntryId++;
    const outgoing = this.effectiveTop();
    this.stack.push({ id, context, callbacks, disabled });
    const incoming = this.effectiveTop();
    if (outgoing !== incoming) {
      this.releaseAll(outgoing);
    }
    return id;
  }

  popContext(id: EntryId): void {
    const idx = this.stack.findIndex((e) => e.id === id);
    if (idx < 0) {
      return;
    }
    const outgoing = this.effectiveTop();
    this.stack.splice(idx, 1);
    const incoming = this.effectiveTop();
    if (outgoing !== incoming) {
      this.releaseAll(outgoing);
    }
  }

  setEntryDisabled(id: EntryId, disabled: boolean): void {
    const entry = this.stack.find((e) => e.id === id);
    if (!entry || entry.disabled === disabled) {
      return;
    }
    const outgoing = this.effectiveTop();
    entry.disabled = disabled;
    const incoming = this.effectiveTop();
    if (outgoing !== incoming) {
      this.releaseAll(outgoing);
    }
  }

  private effectiveTop(): StackEntry | undefined {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      if (!this.stack[i].disabled) {
        return this.stack[i];
      }
    }
    return undefined;
  }

  private releaseAll(suspendedEntry: StackEntry | undefined): void {
    this.keysJustPressed.clear();
    this.keysJustReleased.clear();
    this.gpButtonsJustPressed.clear();
    if (suspendedEntry) {
      suspendedEntry.callbacks.onSuspended?.();
    }
  }

  isActionActive(context: GameplayContext, action: AnyAction): boolean {
    if (this.effectiveTop()?.context !== context) {
      return false;
    }
    const mapping = this.getMapping(context, action);
    if (!mapping) {
      return false;
    }
    const mods = this.getKnownModifiers(context);
    return (
      (this.isKeyboardBindingActive(mapping.keyboard) &&
        this.keyboardModifierExclusivityOk(mapping.keyboard, mods.keyboard)) ||
      (this.isGamepadBindingActive(mapping.gamepad) &&
        this.gamepadModifierExclusivityOk(mapping.gamepad, mods.gamepad))
    );
  }

  isActionJustPressed(context: GameplayContext, action: AnyAction): boolean {
    if (this.effectiveTop()?.context !== context) {
      return false;
    }
    const mapping = this.getMapping(context, action);
    if (!mapping) {
      return false;
    }
    const mods = this.getKnownModifiers(context);
    return (
      (this.isKeyboardBindingJustPressed(mapping.keyboard) &&
        this.keyboardModifierExclusivityOk(mapping.keyboard, mods.keyboard)) ||
      (this.isGamepadBindingJustPressed(mapping.gamepad) &&
        this.gamepadModifierExclusivityOk(mapping.gamepad, mods.gamepad))
    );
  }

  isActionJustReleased(context: GameplayContext, action: AnyAction): boolean {
    if (this.effectiveTop()?.context !== context) {
      return false;
    }
    const mapping = this.getMapping(context, action);
    if (!mapping) {
      return false;
    }
    return this.isKeyboardBindingJustReleased(mapping.keyboard);
  }

  isKeyDown(code: string): boolean {
    return this.keysDown.has(code);
  }

  getAxis(axisIndex: number): number {
    return this.gpAxes[axisIndex] ?? 0;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) {
      this.dispatchKeyboard(e, 'repeat');
      return;
    }

    // Modifier-aware lookup: a combo hotkey is registered as `Ctrl+KeyE`
    // (fixed modifier order Ctrl→Shift→Alt). Falls back to the bare code so
    // plain F-keys keep working with or without modifiers held.
    const comboCode =
      (e.ctrlKey ? 'Ctrl+' : '') +
      (e.shiftKey ? 'Shift+' : '') +
      (e.altKey ? 'Alt+' : '') +
      e.code;
    const hotkey = this.hotkeys.get(comboCode) ?? this.hotkeys.get(e.code);
    if (hotkey) {
      e.preventDefault();
      hotkey();
      return;
    }

    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
      return;
    }

    // Tab and Backspace have browser defaults (focus change, back history) we
    // never want in gameplay. Block them early, before any mapping resolution
    // — covers cases where the key is not bound to an action or where the
    // active context does not consume it.
    if (e.code === 'Tab' || e.code === 'Backspace') {
      e.preventDefault();
    }

    this.keysDown.add(e.code);
    this.keysJustPressed.add(e.code);
    useInputSourceStore.getState().setLastSource('keyboard');
    this.dispatchKeyboard(e, 'press');
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keysDown.delete(e.code);
    this.keysJustReleased.add(e.code);
    this.commitCaptureModifierOnRelease(e.code);
  };

  // A deferred modifier released with no other key pressed meanwhile commits as
  // a lone-modifier binding. If another key had been pressed, the combo already
  // committed at that keydown and `captureCandidate` is null.
  private commitCaptureModifierOnRelease(code: string): void {
    if (this.captureCandidate !== code) {
      return;
    }
    this.captureCandidate = null;
    const top = this.effectiveTop();
    if (top?.context !== 'key-capture') {
      return;
    }
    top.callbacks.onCapture?.({ kind: 'keyboard', code, modifiers: [] });
    this.popContext(top.id);
  }

  private onBlur = (): void => {
    const top = this.effectiveTop();
    this.captureCandidate = null;
    this.keysDown.clear();
    this.keysJustPressed.clear();
    this.keysJustReleased.clear();
    this.gpButtonsDown.clear();
    this.gpButtonsJustPressed.clear();
    this.gpPrevAxesActive.clear();
    top?.callbacks.onSuspended?.();
  };

  private pollLoop = (): void => {
    this.pollGamepad();
    this.rafId = requestAnimationFrame(this.pollLoop);
  };

  private pollGamepad(): void {
    const pads = navigator.getGamepads();
    const pad = pads[0] ?? pads[1] ?? pads[2] ?? pads[3];
    if (!pad) {
      return;
    }

    let gamepadActivity = false;

    for (let i = 0; i < pad.buttons.length; i++) {
      const pressed = pad.buttons[i].pressed;
      const wasDown = this.gpButtonsDown.has(i);

      if (pressed && !wasDown) {
        this.gpButtonsDown.add(i);
        this.gpButtonsJustPressed.add(i);
        gamepadActivity = true;
        this.dispatchGamepadButton(i);
      } else if (!pressed && wasDown) {
        this.gpButtonsDown.delete(i);
      } else if (pressed) {
        gamepadActivity = true;
      }
    }

    for (let i = 0; i < pad.axes.length; i++) {
      const v = pad.axes[i];
      this.gpAxes[i] = v;
      if (Math.abs(v) > AXIS_DEADZONE) {
        gamepadActivity = true;
      }
    }

    if (gamepadActivity) {
      useInputSourceStore.getState().setLastSource('gamepad');
    }
  }

  private dispatchKeyboard(e: KeyboardEvent, kind: ActionKind): void {
    const top = this.effectiveTop();
    if (!top) {
      return;
    }

    if (top.context === 'key-capture') {
      if (kind !== 'press') {
        return;
      }
      const isModifier =
        e.code.startsWith('Shift') ||
        e.code.startsWith('Control') ||
        e.code.startsWith('Alt') ||
        e.code.startsWith('Meta');
      if (isModifier) {
        // Defer: this may be a lone modifier (commits on keyup) or the start of
        // a combo (commits when the next key is pressed below). `size === 1`
        // means it is currently the only key held → a valid lone candidate.
        this.captureCandidate = this.keysDown.size === 1 ? e.code : null;
        return;
      }
      const modifiers: string[] = [];
      if (e.shiftKey) {
        modifiers.push('ShiftLeft');
      }
      if (e.altKey) {
        modifiers.push('AltLeft');
      }
      if (e.ctrlKey) {
        modifiers.push('ControlLeft');
      }
      this.captureCandidate = null;
      const id = top.id;
      top.callbacks.onCapture?.({
        kind: 'keyboard',
        code: e.code,
        modifiers,
      });
      this.popContext(id);
      return;
    }

    if (!top.callbacks.onAction) {
      return;
    }
    if (kind === 'repeat' && !this.isRepeatable(top, e)) {
      return;
    }

    const state = useControlsStore.getState();
    const contextMap =
      top.context === 'interface' ? state.interface : state.dungeon;
    const knownMods = this.getKnownModifiers(top.context);
    for (const action in contextMap) {
      const m = (contextMap as Record<string, ActionMapping>)[action];
      const kb = m.keyboard;
      if (kb.code !== e.code) {
        continue;
      }
      if (!this.keyboardModifierMatches(kb, e)) {
        continue;
      }
      if (!this.keyboardModifierExclusivityOk(kb, knownMods.keyboard)) {
        continue;
      }
      e.preventDefault();
      top.callbacks.onAction(action as AnyAction, kind);
    }
  }

  private keyboardModifierMatches(
    binding: KeyboardBinding,
    e: KeyboardEvent,
  ): boolean {
    const mods = binding.modifiers ?? [];
    const hasShift = mods.some((m) => m.startsWith('Shift'));
    const hasAlt = mods.some((m) => m.startsWith('Alt'));
    const hasCtrl = mods.some((m) => m.startsWith('Control'));
    // When the pressed key IS a modifier (lone Shift/Ctrl/Alt binding), the
    // event's modifier flag is trivially set — don't penalise a bare binding.
    const pressedIsShift = e.code.startsWith('Shift');
    const pressedIsAlt = e.code.startsWith('Alt');
    const pressedIsCtrl = e.code.startsWith('Control');
    return (
      (hasShift || pressedIsShift) === e.shiftKey &&
      (hasAlt || pressedIsAlt) === e.altKey &&
      (hasCtrl || pressedIsCtrl) === e.ctrlKey
    );
  }

  private isRepeatable(top: StackEntry, e: KeyboardEvent): boolean {
    const repeatable = top.callbacks.repeatable;
    if (!repeatable || repeatable.length === 0) {
      return false;
    }
    const state = useControlsStore.getState();
    const contextMap =
      top.context === 'interface' ? state.interface : state.dungeon;
    for (const action of repeatable) {
      const m = (contextMap as Record<string, ActionMapping>)[action];
      if (!m) {
        continue;
      }
      if (
        m.keyboard.code === e.code &&
        this.keyboardModifierMatches(m.keyboard, e)
      ) {
        return true;
      }
    }
    return false;
  }

  private dispatchGamepadButton(index: number): void {
    const top = this.effectiveTop();
    if (!top) {
      return;
    }

    if (top.context === 'key-capture') {
      const id = top.id;
      top.callbacks.onCapture?.({ kind: 'gamepad', index });
      this.popContext(id);
      return;
    }

    if (!top.callbacks.onAction) {
      return;
    }

    const state = useControlsStore.getState();
    const contextMap =
      top.context === 'interface' ? state.interface : state.dungeon;
    const knownMods = this.getKnownModifiers(top.context);
    for (const action in contextMap) {
      const m = (contextMap as Record<string, ActionMapping>)[action];
      const gp = m.gamepad;
      if (!this.gamepadBindingMatchesIndex(gp, index)) {
        continue;
      }
      if (!this.gamepadModifierExclusivityOk(gp, knownMods.gamepad)) {
        continue;
      }
      top.callbacks.onAction(action as AnyAction, 'press');
    }
  }

  private gamepadBindingMatchesIndex(
    binding: GamepadBinding,
    index: number,
  ): boolean {
    if (binding.type === 'button') {
      return binding.index === index;
    }
    if (binding.type === 'combo') {
      return (
        binding.button === index && this.gpButtonsDown.has(binding.modifier)
      );
    }
    return false;
  }

  private isKeyboardBindingActive(binding: KeyboardBinding): boolean {
    if (!this.keysDown.has(binding.code)) {
      return false;
    }
    if (binding.modifiers) {
      for (const mod of binding.modifiers) {
        if (!this.keysDown.has(mod)) {
          return false;
        }
      }
    }
    return true;
  }

  private isKeyboardBindingJustPressed(binding: KeyboardBinding): boolean {
    if (!this.keysJustPressed.has(binding.code)) {
      return false;
    }
    if (binding.modifiers) {
      for (const mod of binding.modifiers) {
        if (!this.keysDown.has(mod)) {
          return false;
        }
      }
    }
    return true;
  }

  private isKeyboardBindingJustReleased(binding: KeyboardBinding): boolean {
    if (!this.keysJustReleased.has(binding.code)) {
      return false;
    }
    return true;
  }

  private isGamepadBindingActive(binding: GamepadBinding): boolean {
    switch (binding.type) {
      case 'button':
        return this.gpButtonsDown.has(binding.index);
      case 'axis': {
        const val = this.gpAxes[binding.axisIndex] ?? 0;
        return binding.direction === 'positive'
          ? val > AXIS_DEADZONE
          : val < -AXIS_DEADZONE;
      }
      case 'combo':
        return (
          this.gpButtonsDown.has(binding.modifier) &&
          this.gpButtonsDown.has(binding.button)
        );
    }
  }

  private isGamepadBindingJustPressed(binding: GamepadBinding): boolean {
    switch (binding.type) {
      case 'button':
        return this.gpButtonsJustPressed.has(binding.index);
      case 'axis': {
        const val = this.gpAxes[binding.axisIndex] ?? 0;
        const active =
          binding.direction === 'positive'
            ? val > AXIS_DEADZONE
            : val < -AXIS_DEADZONE;
        const key = `${binding.axisIndex}:${binding.direction}`;
        const wasActive = this.gpPrevAxesActive.has(key);
        if (active && !wasActive) {
          this.gpPrevAxesActive.add(key);
          return true;
        }
        if (!active && wasActive) {
          this.gpPrevAxesActive.delete(key);
        }
        return false;
      }
      case 'combo':
        return (
          this.gpButtonsDown.has(binding.modifier) &&
          this.gpButtonsJustPressed.has(binding.button)
        );
    }
  }

  private getMapping(
    context: GameplayContext,
    action: AnyAction,
  ): ActionMapping | undefined {
    const state: ControlsState = useControlsStore.getState();
    const contextMap =
      context === 'interface' ? state.interface : state.dungeon;
    return (contextMap as Record<string, ActionMapping>)[action];
  }

  /**
   * Enumerates keys/buttons that act as a modifier in at least one combo
   * binding of the context. Lets us prevent a plain binding (no modifier) from
   * firing while the user holds a modifier — e.g. RB+X must not trigger
   * INVENTORY, Shift+Tab must not trigger TAB_NEXT.
   */
  private getKnownModifiers(context: GameplayContext): {
    keyboard: string[];
    gamepad: number[];
  } {
    const state: ControlsState = useControlsStore.getState();
    const map = context === 'interface' ? state.interface : state.dungeon;
    const keyboardSet: Record<string, true> = {};
    const gamepadSet: Record<number, true> = {};
    for (const action in map) {
      const m = (map as Record<string, ActionMapping>)[action];
      if (m.keyboard.modifiers) {
        for (const k of m.keyboard.modifiers) {
          keyboardSet[k] = true;
        }
      }
      if (m.gamepad.type === 'combo') {
        gamepadSet[m.gamepad.modifier] = true;
      }
    }
    return {
      keyboard: Object.keys(keyboardSet),
      gamepad: Object.keys(gamepadSet).map((s) => Number(s)),
    };
  }

  /**
   * A keyboard binding without its own modifier is only valid if NO known
   * modifier is held. If the binding has its own modifiers, they were already
   * checked by `isKeyboardBinding*`, so accept directly.
   *
   * Edge case: if the binding itself uses a key that also serves as a modifier
   * (e.g. LOCK_DIAGONAL = ShiftLeft, which is also the modifier of TAB_PREV),
   * the key does not block itself.
   */
  private keyboardModifierExclusivityOk(
    binding: KeyboardBinding,
    knownModifiers: string[],
  ): boolean {
    if (binding.modifiers && binding.modifiers.length > 0) {
      return true;
    }
    for (let i = 0; i < knownModifiers.length; i++) {
      const mod = knownModifiers[i];
      if (mod === binding.code) {
        continue;
      }
      if (this.keysDown.has(mod)) {
        return false;
      }
    }
    return true;
  }

  /**
   * A gamepad `button` binding is only valid if NO known gamepad modifier
   * (LB/RB for skill combos, etc.) is held. `combo` and `axis` bindings are
   * not affected.
   *
   * Same edge case as `keyboardModifierExclusivityOk`: a button that is itself
   * a modifier does not block itself. Lets LOCK_DIAGONAL = RB coexist with
   * SKILL_1..4 = LB+...
   */
  private gamepadModifierExclusivityOk(
    binding: GamepadBinding,
    knownModifiers: number[],
  ): boolean {
    if (binding.type !== 'button') {
      return true;
    }
    for (let i = 0; i < knownModifiers.length; i++) {
      const mod = knownModifiers[i];
      if (mod === binding.index) {
        continue;
      }
      if (this.gpButtonsDown.has(mod)) {
        return false;
      }
    }
    return true;
  }
}

export const inputService = new InputService();
